import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { zusToast } from '@zep/toast';

import { useOpenWindow } from '@/hooks';
import { useUser } from '@/lib/auth';
import { setReferrer } from '@/shared/features/enter-referrer-tracking';
import { copyQuizToLocation } from '@/shared/features/quiz-folder/api';
import type { FolderCopyPlace } from '@/shared/features/quiz-folder/consts/events.quiz-folder';
import { showQuizSaveSuccess } from '@/shared/features/quiz-folder/utils/quizSaveFeedback';
import { invalidateMyQuizList } from '@/shared/features/quiz-list/utils/myQuizListCache';
import {
  type QuizCopyContext,
  type QuizCopySource,
} from '@/shared/tracking/quizCopySource';

import {
  buildCopyPath,
  type CopyHandoffMessage,
  isCopyHandoffMessage,
} from '../utils/copyHandoff';
import { saveCopyIntent } from '../utils/copyIntent';
import { trackQuizCopyAuthStart } from '../utils/trackCopyFunnel';
import {
  toCopyPlace,
  trackCopyOutcome,
  trackCopySaveEntry,
} from '../utils/trackCopySave';

/**
 * 진행 중인 복사 요청. 복사 버튼이 여러 진입점에 있어도 같은 원본이 두 번 복사되지 않게
 * 훅 인스턴스를 가로질러 **동기적으로** 잠근다 — state 는 다음 렌더까지 연타를 못 막는다.
 */
const copyingQuizIds = new Set<string>();

/**
 * 인증을 맡긴 탭 → 그 탭의 결과를 보여 줄 함수.
 *
 * 훅 인스턴스가 아니라 모듈에 둔다 — 공유 모달처럼 `복사하기` 를 누르자마자 닫히는 진입점에서도
 * 결과를 받아야 한다.
 */
const pendingHandoffs = new Map<
  Window,
  (message: CopyHandoffMessage) => void
>();
let isHandoffListenerAttached = false;

/** 결과 수신 리스너를 한 번만 붙인다. 페이지가 살아 있는 동안 떼지 않는다. */
const ensureHandoffListener = () => {
  if (isHandoffListenerAttached) return;
  isHandoffListenerAttached = true;

  window.addEventListener('message', (event: MessageEvent<unknown>) => {
    // 같은 오리진 + 내가 연 창. 확장 프로그램·다른 iframe 메시지를 거른다.
    if (event.origin !== window.location.origin) return;
    const present = pendingHandoffs.get(event.source as Window);
    if (!present || !isCopyHandoffMessage(event.data)) return;

    pendingHandoffs.delete(event.source as Window);
    present(event.data);
  });
};

/**
 * 이 탭이 화면에 보일 때 한 번 실행한다. 이미 보이면 바로.
 *
 * 인증 탭이 결과를 보내는 순간 사용자는 아직 그 탭에 있다. 돌아온 순간에 토스트를 띄워야 놓치지 않는다.
 */
const runWhenTabVisible = (run: () => void) => {
  if (document.visibilityState === 'visible') {
    run();
    return;
  }
  const handleVisible = () => {
    if (document.visibilityState !== 'visible') return;
    document.removeEventListener('visibilitychange', handleVisible);
    run();
  };
  document.addEventListener('visibilitychange', handleVisible);
};

/** 아직 열려 있는 인증 탭. 로그인 없이 닫힌 탭은 결과가 오지 않으므로 이때 걷어 낸다. */
const findOpenAuthTab = () => {
  for (const tab of pendingHandoffs.keys()) {
    if (!tab.closed) return tab;
    pendingHandoffs.delete(tab);
  }
  return null;
};

/** 이 훅 안에서는 자리(`copyPlace`)를 항상 안다 — `location` 에서 바로 만든다. */
type ResolvedCopyContext = QuizCopyContext & { copyPlace: FolderCopyPlace };

export interface CopyQuizOptions {
  /**
   * 복사 경로까지 따라갈 유입 출처. 넘기면 URL 로 실려 인증 왕복을 넘어 살아남는다.
   *
   * `location`(= referrer)은 TTL 5분이라 회원가입을 넘기면 사라진다.
   */
  copySource?: QuizCopySource;
  /** 공유 클릭 한 번을 끝까지 잇는 시도 ID. 인증 왕복을 넘어 저장까지 따라간다. */
  shareFlowId?: string;
  /** 체험 제한 화면 복사를 체험 세션에 잇는 ID. */
  trialSessionId?: string;
}

/**
 * 플레이 주변(공유 모달·체험 제한·튜토리얼·젤리)의 `복사하기`.
 *
 * - 로그인 상태 → 탭을 열지 않고 제자리에서 복사하고 토스트만 띄운다.
 * - 게스트 → 새 탭에 인증을 맡긴다. 그 탭이 복사까지 끝내고 결과를 돌려준다(`/copy/[quizsetId]`).
 *   원탭은 사용자가 돌아온 순간 토스트로 결과를 보여 준다. 학생 차단은 원탭에 아무것도 띄우지 않는다.
 *
 * 게스트의 복사를 원탭이 하지 않는 이유는 `copyHandoff.ts` 참고.
 */
export const useCopyQuiz = ({
  onResultPresented,
}: {
  /**
   * 인증 탭의 결과를 이 탭에서 알린 뒤 한 번. 플레이에서는 `useRequestPlayIframeFocus()` 를 넘겨
   * 게임이 키보드 포커스를 되찾게 한다.
   */
  onResultPresented?: () => void;
} = {}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  /* `common` 은 모든 페이지가 로드한다 — 진입점마다 네임스페이스를 챙기지 않아도 된다. */
  const { t } = useTranslation(['common']);
  const { user } = useUser();
  const openWindow = useOpenWindow();
  const [isCopying, setIsCopying] = useState(false);

  // 성공·실패 토스트는 진입 방식(제자리 복사 / 인증 탭 결과)과 무관하게 같아야 한다.
  const showCopied = useCallback(
    (copiedQuizsetId: string) =>
      showQuizSaveSuccess({
        title: t('common:folderSave.success'),
        href: `/quiz/${copiedQuizsetId}`,
        actionLabel: t('common:quizCopy.viewQuiz'),
        onNavigate: href => void router.push(href),
      }),
    [router, t],
  );
  const showCopyFailed = useCallback(
    () =>
      zusToast.error(t('common:quizDetail.copy.failed'), {
        position: 'top-center',
      }),
    [t],
  );

  const presentResult = useCallback(
    (message: CopyHandoffMessage) => {
      // 사본은 다른 탭(인증 탭)에서 만들어졌다. 이 탭의 내 퀴즈 목록 캐시를 맞춘다.
      if (message.result === 'copied') invalidateMyQuizList(queryClient);

      runWhenTabVisible(() => {
        try {
          if (message.result === 'failed') showCopyFailed();
          if (message.result === 'copied') showCopied(message.copiedQuizsetId);
          // 'blocked' 는 무음 — 인증 탭이 같은 말을 차단 화면으로 이미 했다.
        } finally {
          // 탭을 오갔으므로 게임이 키보드 포커스를 잃은 상태다. 돌려주지 않으면 조용히 먹통이 된다.
          onResultPresented?.();
        }
      });
    },
    [onResultPresented, queryClient, showCopied, showCopyFailed],
  );

  /** 로그인 상태. 탭을 열지 않고 제자리에서 복사한다. */
  const copyAsSignedIn = useCallback(
    async (quizId: string, copyContext: ResolvedCopyContext) => {
      if (copyingQuizIds.has(quizId)) return;
      copyingQuizIds.add(quizId);
      trackCopySaveEntry(copyContext.copyPlace, quizId, {
        authRequired: false,
      });
      setIsCopying(true);
      try {
        const copied = await copyQuizToLocation({ sourceQuizsetId: quizId });
        trackCopyOutcome(copyContext, quizId, {
          status: 'success',
          copiedQuizsetId: copied.id,
        });
        invalidateMyQuizList(queryClient);
        showCopied(copied.id);
      } catch (error) {
        trackCopyOutcome(copyContext, quizId, { status: 'fail', error });
        showCopyFailed();
      } finally {
        copyingQuizIds.delete(quizId);
        setIsCopying(false);
      }
    },
    [queryClient, showCopied, showCopyFailed],
  );

  /**
   * 게스트. 새 탭에 인증을 맡기고 결과를 기다린다.
   *
   * 잠그지 않는다 — 인증 탭을 로그인 없이 닫으면 결과 메시지가 오지 않아 풀 시점이 없다.
   * 대신 아직 열려 있는 인증 탭이 있으면 새로 열지 않고 그 탭을 앞으로 가져온다.
   */
  const openAuthTabForGuest = useCallback(
    (quizId: string, copyContext: ResolvedCopyContext) => {
      const openAuthTab = findOpenAuthTab();
      if (openAuthTab) {
        openAuthTab.focus();
        return;
      }

      trackCopySaveEntry(copyContext.copyPlace, quizId, { authRequired: true });
      trackQuizCopyAuthStart(copyContext, quizId);

      const loginUrl = `/login?callbackUrl=${encodeURIComponent(
        buildCopyPath(quizId, copyContext),
      )}`;
      // 착지 경로는 이 표식이 있을 때만 확인 없이 복사한다(새로고침·링크 직접 진입 차단).
      saveCopyIntent(quizId);
      /*
        클릭에서 **동기적으로** 열어야 브라우저가 막지 않는다. `await` 뒤로 미루면 차단된다.
        막히면 `null` 이라, 현재 탭을 보내 착지 경로가 opener 없이 처리하게 한다.
      */
      const authTab = openWindow(loginUrl);
      if (!authTab) {
        void router.push(loginUrl);
        return;
      }
      // 이 컴포넌트가 곧 사라져도(공유 모달) 결과는 모듈 리스너가 받아 이 함수로 보여 준다.
      pendingHandoffs.set(authTab, presentResult);
      ensureHandoffListener();
    },
    [openWindow, presentResult, router],
  );

  const copyQuiz = useCallback(
    (quizId: string, location: string, options?: CopyQuizOptions) => {
      setReferrer({ from: '/play', location });

      const copyContext: ResolvedCopyContext = {
        copyPlace: toCopyPlace(location),
        copySource: options?.copySource,
        shareFlowId: options?.shareFlowId,
        trialSessionId: options?.trialSessionId,
      };

      if (user) {
        void copyAsSignedIn(quizId, copyContext);
        return;
      }
      openAuthTabForGuest(quizId, copyContext);
    },
    [user, copyAsSignedIn, openAuthTabForGuest],
  );

  return { copyQuiz, isCopying };
};
